import React, { useEffect, useMemo, useState } from 'react';
import type { ChangeEventHandler, ComponentProps } from 'react';
import styled from '@emotion/styled';
import { uniqueId } from 'lodash-es';
import { themeGet } from 'styled-system';

import { FieldChangeTrigger } from '../../enums/form';
import type { ThemedComponent } from '../../theme/theme';
import type { FieldChangeTriggerHandler } from '../../types/form';
import { Wrapper } from '../Input/StatefulInput';
import { TRANSITION_CSS } from '../Input/StatefulInput/constants';
import { ThemeTextColor } from '../Input/StatefulInput/enums';
import {
  applyInputCSS,
  applyInputWithStateCSS,
  inputDisabledCSS,
  inputFocusCSS,
} from '../Input/StatefulInput/InnerInput';
import Label from '../Input/StatefulInput/Label';
import StateMessages from '../Input/StatefulInput/StateMessages';
import Textarea from '../Textarea';
import { isValueValid } from './utils';

export interface TextareaFieldProps<FieldName extends string = string> {
  label: string;
  name: FieldName;
  value?: string;
  onChange?: FieldChangeTriggerHandler<FieldName>;
  hasError?: boolean;
  errors?: string[];
  regex?: RegExp;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  trimValue?: boolean;
}

const StyledTextarea: ThemedComponent<
  ComponentProps<typeof Textarea> | Pick<TextareaFieldProps, 'hasError'>
> = styled(Textarea)<any>`
  ${TRANSITION_CSS};

  &::placeholder {
    ${TRANSITION_CSS};
    color: transparent;
  }

  &:focus::placeholder {
    color: ${themeGet(`colors.${ThemeTextColor.Placeholder}`)};
  }

  &:not(:disabled) {
    &:active,
    &:focus {
      ${inputFocusCSS};
    }
  }

  &:disabled {
    ${inputDisabledCSS};
  }

  ${(props: any) => applyInputCSS(props)}
  ${applyInputWithStateCSS}
`;

const TextareaField = ({
  label,
  name,
  disabled,
  hasError,
  errors,
  value = '',
  onChange,
  regex,
  maxLength,
  required,
  trimValue,
}: TextareaFieldProps) => {
  const [inputValue, setInputValue] = useState(value);
  const id = useMemo(() => uniqueId('input-'), []);
  const stateMessagesId = `${id}-state-messages`;

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = ({
    target: { value: newValue },
  }) => {
    if (isValueValid({ value: newValue, regex, maxLength })) {
      setInputValue(newValue);
      onChange({
        fieldName: name,
        value: newValue,
        trigger: FieldChangeTrigger.Change,
      });
    }
  };

  const handleBlur = () =>
    onChange({
      fieldName: name,
      value: trimValue ? inputValue.trim() : inputValue,
      trigger: FieldChangeTrigger.Blur,
    });

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  return (
    <Wrapper value={value} disabled={disabled} hasError={hasError} width={1} position="relative">
      <StyledTextarea
        id={id}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        name={name}
        placeholder={label}
        aria-label={label}
        aria-invalid={hasError}
        aria-required={required ?? false}
        hasError={hasError}
        {...(errors?.length > 0 && {
          'aria-describedby': stateMessagesId,
        })}
      />
      {label && (
        <Label htmlFor={id} value={value}>
          {label}
        </Label>
      )}
      <StateMessages id={stateMessagesId} errors={errors} hasError={hasError} />
    </Wrapper>
  );
};
export default TextareaField;
